import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  CircularProgress,
} from '@mui/material';
import { Visibility as ViewIcon, Email as EmailIcon, Book as BookIcon } from '@mui/icons-material';
import { useUsers, User, EntitlementWithDetails } from '../hooks';
// Removed unused imports: formatFirestoreDate, formatFirestoreDateOnly

const UsersPage: React.FC = () => {
  const { users, loading, error, getUserEntitlements } = useUsers();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEntitlementsDialog, setOpenEntitlementsDialog] = useState(false);
  const [userEntitlements, setUserEntitlements] = useState<EntitlementWithDetails[]>([]);
  const [entitlementsLoading, setEntitlementsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
  };

  const handleViewEntitlements = async (user: User) => {
    try {
      setEntitlementsLoading(true);
      setSelectedUser(user);
      const entitlements = await getUserEntitlements(user.id);
      setUserEntitlements(entitlements);
      setOpenEntitlementsDialog(true);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Erro ao carregar entitlements',
        severity: 'error',
      });
    } finally {
      setEntitlementsLoading(false);
    }
  };

  const handleCloseEntitlementsDialog = () => {
    setOpenEntitlementsDialog(false);
    setSelectedUser(null);
    setUserEntitlements([]);
  };

  const formatCPF = (cpf: string | undefined | null) => {
    if (!cpf || cpf.trim() === '') return 'N/A';
    try {
      // Remove caracteres não numéricos
      const cleanCpf = cpf.replace(/\D/g, '');
      if (cleanCpf.length === 11) {
        return cleanCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      }
      return cpf; // Retorna o CPF original se não tiver 11 dígitos
    } catch {
      return 'N/A';
    }
  };

  const formatPhone = (phone: string | undefined | null) => {
    if (!phone || phone.trim() === '') return 'N/A';
    try {
      // Remove caracteres não numéricos
      const cleanPhone = phone.replace(/\D/g, '');
      if (cleanPhone.length === 11) {
        return cleanPhone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
      } else if (cleanPhone.length === 10) {
        return cleanPhone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
      }
      return phone; // Retorna o telefone original se não tiver formato esperado
    } catch {
      return 'N/A';
    }
  };

  const getProviderIcon = (provider: string | undefined | null) => {
    if (!provider || provider.trim() === '') return '👤';
    switch (provider.toLowerCase()) {
      case 'google':
        return '🔍';
      case 'facebook':
        return '📘';
      case 'password':
        return '🔐';
      case 'apple':
        return '🍎';
      default:
        return '👤';
    }
  };

  const getProviderLabel = (provider: string | undefined | null) => {
    if (!provider || provider.trim() === '') return 'N/A';
    switch (provider.toLowerCase()) {
      case 'google':
        return 'Google';
      case 'facebook':
        return 'Facebook';
      case 'password':
        return 'Email/Senha';
      case 'apple':
        return 'Apple';
      default:
        return provider;
    }
  };

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    try {
      // Se for um timestamp do Firestore
      if (date.seconds) {
        return new Date(date.seconds * 1000).toLocaleDateString('pt-BR');
      }
      // Se for uma string de data
      if (typeof date === 'string') {
        return new Date(date).toLocaleDateString('pt-BR');
      }
      // Se for um objeto Date
      if (date instanceof Date) {
        return date.toLocaleDateString('pt-BR');
      }
      return 'Data inválida';
    } catch {
      return 'Data inválida';
    }
  };

  const formatDateTime = (date: any) => {
    if (!date) return 'N/A';
    try {
      // Se for um timestamp do Firestore
      if (date.seconds) {
        return new Date(date.seconds * 1000).toLocaleString('pt-BR');
      }
      // Se for uma string de data
      if (typeof date === 'string') {
        return new Date(date).toLocaleString('pt-BR');
      }
      // Se for um objeto Date
      if (date instanceof Date) {
        return date.toLocaleString('pt-BR');
      }
      return 'Data inválida';
    } catch {
      return 'Data inválida';
    }
  };

  const getTitleTypeLabel = (type: string | undefined | null) => {
    if (!type) return 'N/A';
    switch (type) {
      case 'ebook':
        return 'E-book';
      case 'audiobook':
        return 'Audiobook';
      default:
        return type;
    }
  };

  const getSourceLabel = (source: string | undefined | null) => {
    if (!source) return 'N/A';
    switch (source) {
      case 'csv_upload':
        return 'Upload CSV';
      case 'payment_webhook':
        return 'Webhook Pagamento';
      case 'manual':
        return 'Manual';
      default:
        return source;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Gerenciar Usuários
        </Typography>
      </Box>

      {/* Painel de Debug */}
      {/* <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Painel de Debug</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Este painel permite criar usuários de teste para simular o cadastro via app. Os usuários reais serão criados automaticamente quando se registrarem
            via Firebase Authentication.
          </Typography>
          <Button variant="outlined" onClick={handleOpenDebugDialog}>
            Criar Usuário de Teste
          </Button>
        </AccordionDetails>
      </Accordion> */}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Usuário</TableCell>
              <TableCell>CPF</TableCell>
              <TableCell>Telefone</TableCell>
              <TableCell>E-mail</TableCell>
              <TableCell>Provedores</TableCell>
              <TableCell>Registrado em</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users && users.length > 0 ? (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar src={user.photo_url || undefined} sx={{ width: 40, height: 40 }}>
                        {(user.display_name || user.name || 'U').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.display_name || user.name || 'Usuário sem nome'}
                        </Typography>
                        {user.birthDate && (
                          <Typography variant="caption" color="text.secondary">
                            Nasc: {formatDate(user.birthDate)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{formatCPF(user.cpf)}</TableCell>
                  <TableCell>{formatPhone(user.phone)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      {user.email || 'E-mail não informado'}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {user.authProviders && user.authProviders.length > 0
                        ? user.authProviders.map((provider, index) => (
                            <Chip
                              key={index}
                              label={getProviderLabel(provider)}
                              size="small"
                              variant="outlined"
                              icon={<span>{getProviderIcon(provider)}</span>}
                            />
                          ))
                        : 'N/A'}
                    </Box>
                  </TableCell>
                  <TableCell>{formatDate(user.created_time)}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleViewUser(user)} color="primary" title="Ver detalhes">
                      <ViewIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleViewEntitlements(user)}
                      color="secondary"
                      disabled={entitlementsLoading}
                      title="Ver livros recebidos"
                    >
                      <BookIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Nenhum usuário encontrado
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para visualizar detalhes do usuário */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Detalhes do Usuário</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar src={selectedUser.photo_url || undefined} sx={{ width: 80, height: 80 }}>
                  {(selectedUser.display_name || selectedUser.name || 'U').charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {selectedUser.display_name || selectedUser.name || 'Usuário sem nome'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID: {selectedUser.id || 'N/A'}
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="CPF" value={formatCPF(selectedUser.cpf)} margin="normal" InputProps={{ readOnly: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Telefone" value={formatPhone(selectedUser.phone)} margin="normal" InputProps={{ readOnly: true }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="E-mail" value={selectedUser.email || 'E-mail não informado'} margin="normal" InputProps={{ readOnly: true }} />
                </Grid>
                {selectedUser.birthDate && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Data de Nascimento"
                      value={formatDate(selectedUser.birthDate)}
                      margin="normal"
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Provedores de Autenticação"
                    value={
                      selectedUser.authProviders && selectedUser.authProviders.length > 0 ? selectedUser.authProviders.map(getProviderLabel).join(', ') : 'N/A'
                    }
                    margin="normal"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Criado em" value={formatDateTime(selectedUser.created_time)} margin="normal" InputProps={{ readOnly: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Última atualização"
                    value={formatDateTime(selectedUser.updatedAt)}
                    margin="normal"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para visualizar entitlements */}
      <Dialog open={openEntitlementsDialog} onClose={handleCloseEntitlementsDialog} maxWidth="md" fullWidth>
        <DialogTitle>Livros Recebidos - {selectedUser?.name || 'Usuário'}</DialogTitle>
        <DialogContent>
          {entitlementsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : userEntitlements.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nenhum livro recebido
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Este usuário ainda não possui nenhum livro ou audiobook concedido.
              </Typography>
            </Box>
          ) : (
            <Box pt={1}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Livro</TableCell>
                      <TableCell>Empresa</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Data de Concessão</TableCell>
                      <TableCell>Origem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userEntitlements.map((entitlement) => (
                      <TableRow key={entitlement.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {entitlement.title?.name || 'Título não encontrado'}
                            </Typography>
                            {entitlement.title?.authors && entitlement.title.authors.length > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                {Array.isArray(entitlement.title.authors) ? entitlement.title.authors.join(', ') : 'Autor não informado'}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{entitlement.company?.name || 'Empresa não encontrada'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getTitleTypeLabel(entitlement.title?.type || '')}
                            size="small"
                            color={entitlement.title?.type === 'audiobook' ? 'secondary' : 'primary'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(entitlement.grantedAt)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={getSourceLabel(entitlement.source)} size="small" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEntitlementsDialog}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificações */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersPage;
